import { AssetProperties } from '../../asset/types/asset.type';
import { CompanyProperties } from '../../company/types/company.type';
import { UserProperties } from '../../user/types/user.type';

//report email template
export type ReportEmailTemplateProperties = {
  templateId?: string;
  templateName: string;
  platform?: string;
  subject?: string;
  externalId?: string;
  htmlContent: any;
  designContent: any;
  status: string;
  templateType?: string;
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date;
};

export const initialReportEmailTemplate: ReportEmailTemplateProperties = {
  templateId: '',
  templateName: '',
  platform: '',
  subject: '',
  externalId: '',
  htmlContent: '',
  designContent: '',
  status: '',
  createdAt: new Date(),
  updatedAt: new Date(),
};

export interface ICreateReportTemplate {
  templateId: string;
  templateName: string;
  platform: string;
  subject: string;
  externalId: string;
  htmlContent: string;
  designContent: string;
  status: string;
}

// report email receiver
export type ReportEmailReceiverProperties = {
  reportEmailReceiverId: string;
  userId: string;
  companyId: string;
  status: string;
  createdByUserId: string;
  userCreator?: {
    name?: string | null;
    firstName?: string | null;
    middleName?: string | null;
    lastName?: string | null;
  };
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date;
  user?: UserProperties;
  company?: CompanyProperties;
};

export const initialReportEmailReceiver: ReportEmailReceiverProperties = {
  reportEmailReceiverId: '',
  userId: '',
  companyId: '',
  status: '',
  createdByUserId: '',
};

export interface ICreateReportEmailReceiver {
  userId: string;
  companyId: string;
  status: string;
}

//report email target asset
export type ReportEmailTargetAssetProperties = {
  reportEmailTargetAssetId: string;
  templateId: string;
  companyId: string;
  assetId: string;
  sendingAt: Date;
  timeFrom: string;
  timeTo: string;
  timezoneOffset: number;
  timeFromDayOffset: number;
  timeToDayOffset: number;
  frequency: string;
  dayOfWeek?: string;
  dateOfMonth?: string;
  dataThresholdMin?: string;
  scheduleAt: string;
  cronJobName: string;
  status: string;
  createdByUserId?: string;
  userCreatorName?: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  asset?: AssetProperties;
  company?: CompanyProperties;
  reportEmailTarget?: any;
};

export const initialReportEmailTargetAsset: ReportEmailTargetAssetProperties = {
  reportEmailTargetAssetId: '',
  templateId: '',
  companyId: '',
  assetId: '',
  sendingAt: new Date(),
  timeFrom: '',
  timeTo: '',
  timezoneOffset: 7,
  timeFromDayOffset: 1,
  timeToDayOffset: 1,
  frequency: '',
  dayOfWeek: '',
  dateOfMonth: '',
  dataThresholdMin: '',
  scheduleAt: '',
  cronJobName: '',
  status: '',
  createdByUserId: '',
  createdAt: new Date(),
  updatedAt: new Date(),
};

export interface ICreateReportEmailTargetAsset {
  reportEmailTargetAssetId: string;
  templateId: string;
  companyId: string;
  assetId: string;
  sendingAt: Date;
  timeFrom: string;
  timeTo: string;
  timezoneOffset: number;
  timeFromDayOffset: number;
  timeToDayOffset: number;
  frequency: string;
  dayOfWeek?: string;
  dateOfMonth?: string;
  dataThresholdMin?: string;
  status: string;
  createdByUserId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ReportEmailTargetProperties {
  reportEmailTargetId: string;
  reportEmailTargetAssetId: string;
  userId?: string;
  email?: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  reportEmailTargetAsset?: ReportEmailTargetAssetProperties;
  user?: UserProperties;
}

export enum ETemplateType {
  DEFAULT = 'default',
  OPERATION_MODE = 'operation_mode',
}

export const TEMPLATE_TYPE_OPTIONS = [
  { label: 'Daily Report', value: ETemplateType.DEFAULT },
  { label: 'Operation Mode', value: ETemplateType.OPERATION_MODE },
];

export enum EEmailSubjectParams {
  asset_name = 'asset_name',
  report_date_range = 'report_date_range',
  report_date_only = 'report_date_only',
}

export enum EEmailBodyParams {
  general_asset_name = 'general_asset_name',
  general_report_generated = 'general_report_generated',
  general_report_date_range = 'general_report_date_range',
  gps_total_cruise = 'gps_total_cruise',
  gps_average_speed = 'gps_average_speed',
  gps_distance = 'gps_distance',
  total_engine_fuel_cons = 'total_engine_fuel_cons',
  total_ae_fuel_cons = 'total_ae_fuel_cons',
  total_fuel_cons = 'total_fuel_cons',
  ae_1running_time = 'ae_1running_time',
  ae_2running_time = 'ae_2running_time',
  ae_3running_time = 'ae_3running_time',
  ae_1fuel_cons = 'ae_1fuel_cons',
  ae_2fuel_cons = 'ae_2fuel_cons',
  ae_3fuel_cons = 'ae_3fuel_cons',
  engine_port_fuel_cons = 'engine_port_fuel_cons',
  engine_starboard_fuel_cons = 'engine_starboard_fuel_cons',
  engine_center_fuel_cons = 'engine_center_fuel_cons',
  engine_port_running_time = 'engine_port_running_time',
  engine_starboard_running_time = 'engine_starboard_running_time',
  engine_center_running_time = 'engine_center_running_time',
  engine_port_rpm = 'engine_port_rpm',
  engine_starboard_rpm = 'engine_starboard_rpm',
  engine_center_rpm = 'engine_center_rpm',
  operation_mode_summary = 'operation_mode_summary',
  operation_mode_detail = 'operation_mode_detail',
  operation_mode_per_engine = 'operation_mode_per_engine',
}

// Params available per template type
export const EMAIL_BODY_PARAMS_BY_TYPE: Record<string, EEmailBodyParams[]> = {
  default: Object.values(EEmailBodyParams).filter(
    (p) => !p.startsWith('operation_mode_'),
  ),
  operation_mode: [
    EEmailBodyParams.general_asset_name,
    EEmailBodyParams.general_report_generated,
    EEmailBodyParams.general_report_date_range,
    EEmailBodyParams.operation_mode_summary,
    EEmailBodyParams.operation_mode_detail,
    EEmailBodyParams.operation_mode_per_engine,
  ],
};

export enum EFilterItem {
  company = 'company',
  asset = 'asset',
  interval = 'interval',
  dateRange = 'dateRange',
}

export type ReportEmailLogProperties = {
  id: number;
  companyId: string;
  companyName: string;
  assetId: string;
  assetName: string;
  receivers: any;
  deviceIds: string;
  percentage: number;
  sendingStatus: string; // sent, pending, failed
  sendingCount: number;
  dataThreshold: number;
  metaTarget: any; // data JSON from "report_email_target_assets" table
  senderType: string; // value => system, user
  metaSender?: any; // system => null; user => stringify(user)
  sendingScheduleDate: Date; // schedule tanggal pengiriman
  url?: string;
  remark: string;
  dataType: number;
  templateType?: string;
  updatedAt: Date;
  createdAt: Date;
};
